import { useRef } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'

import { Button } from '@mdxui/primitives'

import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'

import { CodeWindow } from '../code-window'
import {
  sectionVariants,
  slideFromLeftVariants,
  slideFromRightVariants,
  reducedMotionVariants,
} from '../../lib/motion'

/**
 * Props for CtaSplitMedia component
 *
 * @remarks
 * **IMPORTANT CONSTRAINTS FOR AI/PROGRAMMATIC GENERATION:**
 * - Split layout with content left, media right
 * - Gradient background on card
 * - Supports both image and code window display
 * - Two buttons: primary + secondary (1-2 regular buttons)
 * - **NEVER use this for install commands - use CtaCenteredCheck instead**
 * - Best for trial/signup CTAs with visual media
 *
 * **CTA Button Rules (CRITICAL):**
 * 1. Use this component for 1-2 regular buttons (primary, optional secondary)
 * 2. Use CtaCenteredCheck for npm/npx/pnpm install commands (InstallSnippet ONLY)
 * 3. NEVER mix InstallSnippet with regular buttons - they are mutually exclusive
 */
export interface CtaSplitMediaProps {
  /** Small label text above heading */
  label?: string
  /** Main heading text */
  heading?: string
  /** Description text below heading */
  description?: string
  /** Button configuration */
  buttons?: {
    primary?: {
      text: string
      url: string
    }
    secondary?: {
      text: string
      url: string
    }
  }
  /** Image configuration */
  image?: {
    src: string
    alt: string
  }
  /** Toggle between image and code window display */
  mediaType?: 'image' | 'code'
  /** Code window configuration when mediaType is 'code' */
  codeWindow?: {
    code: string
    language: string
  }
  /** Code window theme */
  codeTheme?: 'dark' | 'light' | 'auto'
  /** Radial gradient + glow background. Set false for a flat split-media look. @default true */
  gradient?: boolean
  /** Additional CSS classes */
  className?: string
}

/**
 * CtaSplitMedia - Split CTA with gradient background and media
 *
 * **Layout:**
 * ```
 * ┌──────────────────────────────────────┐
 * │  ┌────────────────┬────────────────┐ │
 * │  │  Label         │                │ │
 * │  │  Heading       │    Image/      │ │
 * │  │  Description   │    Code        │ │
 * │  │  [Primary]     │                │ │
 * │  │  [Secondary]   │                │ │
 * │  └────────────────┴────────────────┘ │
 * └──────────────────────────────────────┘
 * ```
 *
 * **Best Use Cases:**
 * - Free trial signup CTAs
 * - Product launch announcements
 * - Premium feature promotions
 *
 * @example
 * ```tsx
 * <CtaSplitMedia
 *   label="Ready to get started?"
 *   heading="Start your free trial today."
 *   description="Start with a 14-day free trial. No credit card required."
 *   buttons={{
 *     primary: { text: 'Get Started', url: '/signup' },
 *     secondary: { text: 'Learn More', url: '/docs' },
 *   }}
 *   image={{ src: '/product.png', alt: 'Product screenshot' }}
 * />
 * ```
 */
const CtaSplitMedia = ({
  label = 'Ready to get started?',
  heading = 'Start your free trial today.',
  description = 'Start with a 14-day free trial. No credit card required. No setup fees. Cancel anytime.',
  buttons = {
    primary: { text: 'Get Started', url: '#' },
  },
  image = {
    src: '/placeholders/placeholder-1.svg',
    alt: 'Product screenshot',
  },
  mediaType = 'code',
  codeWindow = {
    code: `import { CTA } from '@mdxui/neo'

export default function Page() {
  return <CTA variant="split-media-gradient" title="Get started" callToAction="Sign up" />
}`,
    language: 'tsx',
  },
  codeTheme = 'dark',
  gradient = true,
  className,
}: CtaSplitMediaProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  const contentAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : slideFromLeftVariants
  const mediaAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : slideFromRightVariants

  return (
    <motion.section
      ref={sectionRef}
      className={cn('py-16 lg:py-32', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="container mx-auto max-w-7xl overflow-hidden">
        <div
          className={cn(
            'relative mx-auto flex max-w-7xl flex-col gap-6 overflow-hidden border border-border md:flex-row',
            gradient
              ? 'rounded-[var(--radius-2xl,calc(var(--radius)*2))] bg-[radial-gradient(ellipse_30%_60%_at_100%_80%,hsl(var(--muted)),transparent)] md:bg-[radial-gradient(ellipse_50%_80%_at_40%_120%,hsl(var(--muted)),transparent)]'
              : 'rounded-[var(--radius-xl,calc(var(--radius)*1.5))] bg-muted/50',
          )}
        >
          <motion.div
            className="min-w-0 max-w-xl self-center p-6 md:p-12 md:text-left"
            variants={contentAnimation}
          >
            {label && (
              <p className="mb-6 text-sm text-muted-foreground">{label}</p>
            )}
            <h2 className="mb-6 break-words text-balance font-semibold text-4xl">
              {heading}
            </h2>
            <div className="break-words">
              <Markdown size="base">{description}</Markdown>
            </div>
            <div className="mt-6 flex flex-col justify-center gap-4 sm:flex-row md:justify-start">
              {buttons?.primary && (
                <Button asChild className={cn('w-full', buttons?.secondary && 'sm:w-auto')}>
                  <a href={buttons.primary.url}>{buttons.primary.text}</a>
                </Button>
              )}
              {buttons?.secondary && (
                <Button variant="outline" asChild className="w-full sm:w-auto">
                  <a href={buttons.secondary.url}>{buttons.secondary.text}</a>
                </Button>
              )}
            </div>
          </motion.div>
          <motion.div
            className="relative -mb-2 sm:-mb-3 -mr-2 ml-6 flex-1 md:mt-8 md:ml-0"
            variants={mediaAnimation}
          >
            {gradient && (
              <div className="absolute -bottom-8 -left-8 -z-10 h-4/5 w-4/5 rounded-tl-[var(--radius-2xl,calc(var(--radius)*2))] rounded-br-[var(--radius-2xl,calc(var(--radius)*2))] bg-foreground/10 blur-2xl" />
            )}
            {mediaType === 'image' ? (
              <img
                src={image.src}
                alt={image.alt}
                className="relative z-10 h-full max-h-[400px] w-full rounded-tl-[var(--radius-xl,calc(var(--radius)*1.5))] object-cover"
              />
            ) : codeWindow ? (
              <CodeWindow
                code={codeWindow.code}
                language={codeWindow.language}
                showHeader={false}
                theme={codeTheme}
                className="relative z-10 h-full max-h-[400px] w-full rounded-tl-[var(--radius-xl,calc(var(--radius)*1.5))]"
              />
            ) : null}
          </motion.div>
        </div>
      </div>
    </motion.section>
  )
}

export { CtaSplitMedia }
